// src/components/TextPlayer/TextPlayer.tsx

import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Drawer, Select, Modal, Input, message, Spin, FloatButton, Menu, Button, Flex } from "antd";
import { Howl } from "howler";
import {
  BackwardOutlined,
  BookOutlined,
  ControlOutlined,
  EditOutlined,
  ForwardOutlined,
  PauseCircleOutlined,
  PauseCircleTwoTone,
  PlayCircleOutlined,
  PlayCircleTwoTone,
  RobotOutlined,
} from "@ant-design/icons";
import styles from "./TextPlayer.module.css";
import { useAppDispatch, useAppSelector } from '../../hooks';
import { setCurrentSentence, setIsPlaying, setPlaybackSpeed } from '../../store/slices/audioSlice';
import eventBus from "../../utils/eventBus";
import { EVENTS } from "../../config/GlobalConfig";
import { fetchNotes, saveNotes } from '../../store/thunks/notesThunks';
import { selectNotes } from '../../store/slices/notesSlice';
import { RootState } from "../../store";
import { useMediaQuery } from "react-responsive";
import log from "loglevel";
import { DEBUG } from "../../remote/config";
import Chatbot from '../Chatbot/Chatbot';
import { canUseAIAssistant, getAIAssistantMaxUsage, getAIAssistantUsageToday } from "../../utils/aiAssistantUsage";
import audioManager from "../../utils/audioManager";

export const TextPlayer: React.FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const dispatch = useAppDispatch();
  const { currentSentence, playbackSpeed, isPlaying, audioItems, loading, error, courseId, lessonId } = useAppSelector((state) => state.audio);
  const { isLogin, isMember, memberType } = useAppSelector((state) => state.user);
  const notes = useAppSelector((state: RootState) => selectNotes(state, courseId, lessonId));
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [isEditNoteOpen, setIsEditNoteOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [localNotes, setLocalNotes] = useState<string>(notes || "");
  const audioRef = useRef<Howl | null>(null);
  const lastHighlightedRef = useRef<string | null>(null);
  const playerContainerRef = useRef<HTMLDivElement | null>(null);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const elementsMapRef = useRef<Map<string, HTMLElement>>(new Map());
  
  const SPEED_OPTIONS = [
    { value: "0.5", label: "0.5x" },
    { value: "0.75", label: "0.75x" },
    { value: "1", label: "1.0x" },
    { value: "1.25", label: "1.25x" },
    { value: "1.5", label: "1.5x" },
    { value: "2", label: "2.0x" },
  ];

  const getElementRef = useCallback((id: string): HTMLElement | null => {
    if (elementsMapRef.current.has(id)) {
      return elementsMapRef.current.get(id) || null;
    }
    
    const element = document.getElementById(id);
    if (element) {
      elementsMapRef.current.set(id, element);
      return element;
    }
    
    return null;
  }, []);

  useEffect(() => {
    return () => {
      audioManager.stopAndUnloadCurrentAudio();
      elementsMapRef.current.clear();
    };
  }, []);
  
  useEffect(() => {
    if (lessonId > 0 && checkIsLogin(false)) {
      log.debug('fetchNotes = ', courseId, lessonId);
      dispatch(fetchNotes({ courseId, lessonId }));
    }
  }, [dispatch, lessonId]);

  useEffect(() => {
    if (notes !== undefined) {
      setLocalNotes(notes.replace(/\\n/g, "\n"));
    }
  }, [notes]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.stop();
      audioRef.current.unload();
      audioRef.current = null;
    }
    dispatch(setIsPlaying(false));
    dispatch(setCurrentSentence(0));
    elementsMapRef.current.clear();
  }, [lessonId, dispatch]);

  const initHowl = async () => {
    audioManager.stopAndUnloadCurrentAudio();
    
    if (!audioItems[currentSentence] || !audioItems[currentSentence].audio) {
      log.error('No audio available for current sentence');
      return;
    }

    const currentAudio = audioItems[currentSentence].audio;
    
    try {
      audioRef.current = await audioManager.createAudioDebounced(currentAudio, {
        rate: playbackSpeed,
        preload: true,
        onload: () => {
          if (audioRef.current) {
            try {
              if (isPlaying) {
                audioRef.current.play();
              }
            } catch (error) {
              log.error('Failed to play audio:', error);
              message.error("音频播放失败，请重试");
            }
          }
        },
        onend: () => {
          if (currentSentence === audioItems.length - 1) {
            dispatch(setCurrentSentence(0));
            dispatch(setIsPlaying(false));
            eventBus.emit(EVENTS.LOAD_NEXT_LESSON, lessonId);
          } else {
            nextSentence();
          }
        },
        onplay: () => {
          setHighlight(currentSentence);
          if (!isPlaying) {
            dispatch(setIsPlaying(true));
          }
        },
        onloaderror: (id, error) => {
          message.error("音频加载失败，请检查网络连接后重试");
          dispatch(setIsPlaying(false));
        },
        onpause: () => {
          if (isPlaying) {
            dispatch(setIsPlaying(false));
          }
        },
        onstop: () => {
          if (currentSentence === audioItems.length - 1) {
            dispatch(setIsPlaying(false));
          }
        }
      }, 200);
    } catch (error) {
      log.error('Failed to create audio:', error);
      message.error("音频初始化失败，请重试");
    }
  };

  const checkIsMember = useCallback((isShow:boolean) => {
    if (!isMember) {
      if (isShow) {
        message.warning("此功能仅对会员开放");
      }
      return false;
    }
    return true;
  }, [isMember]);

  const checkIsLogin = useCallback((isShow:boolean) => {
    if (!isLogin) {
      if (isShow) {
        message.warning("请先登录");
      }
      return false;
    }
    return true;
  }, [isLogin]);

  const togglePlayPause = () => {
    if (!checkIsLogin(true)) return;
    if (!checkIsMember(true)) return;

    if (audioRef.current) {
      try {
        if (audioRef.current.playing()) {
          audioRef.current.pause();
          dispatch(setIsPlaying(false));
        } else {
          audioRef.current.play();
          dispatch(setIsPlaying(true));
        }
      } catch (error) {
        message.error("播放控制失败，请重试");
      }
    } else {
      message.warning("音频未加载，请等待或刷新页面");
    }
  };

  const changeSpeed = (speed: string) => {
    if (!checkIsLogin(true)) return;
    if (!checkIsMember(true)) return;

    const newSpeed = parseFloat(speed);
    dispatch(setPlaybackSpeed(newSpeed));
    if (audioRef.current) {
      const currentPosition = audioRef.current.seek();
      const wasPlaying = audioRef.current.playing();
      audioRef.current.rate(newSpeed);
      audioRef.current.seek(currentPosition);
      if (wasPlaying) {
        audioRef.current.play();
      }
    }
  };

  const nextSentence = () => {
    if (!checkIsLogin(true)) return;
    if (!checkIsMember(true)) return;

    const nextSentence = (currentSentence + 1) % audioItems.length;
    dispatch(setCurrentSentence(nextSentence));
  };

  const prevSentence = () => {
    if (!checkIsLogin(true)) return;
    if (!checkIsMember(true)) return;

    const prevSentence = (currentSentence + audioItems.length - 1) % audioItems.length;
    dispatch(setCurrentSentence(prevSentence));
  };

  const editClick = useCallback(() => {
    if (!checkIsLogin(true)) return;
    setInputValue(localNotes);
    setIsEditNoteOpen(true);
  }, [localNotes]);

  const handleOk = async () => {
    try {
      const newNotes = inputValue.replace(/\n/g, "\\n");
      await dispatch(saveNotes({ courseId, lessonId, notes: newNotes })).unwrap();
      setLocalNotes(newNotes);
      message.success("笔记保存成功");
    } catch (error) {
      message.error("保存笔记失败，请重试");
    } finally {
      setIsEditNoteOpen(false);
      setInputValue("");
    }
  };

  const handleCancel = useCallback(() => {
    setIsEditNoteOpen(false);
    setInputValue("");
  }, []);

  const showNoteDrawer = () => {
    if (!checkIsLogin(true)) return;
    setIsNoteOpen(true);
  };

  const onCloseDrawer = () => {
    setIsNoteOpen(false);
  };

  const scrollToElement = useCallback((elementId: string) => {
    const element = getElementRef(elementId);
    if (!element) return;

    const playerHeight = playerContainerRef.current?.getBoundingClientRect().height || 0;
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const adjustedWindowHeight = windowHeight - playerHeight;

    const checkVisibility = () => {
      const rect = element.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.top <= adjustedWindowHeight &&
        rect.bottom >= 0 &&
        rect.bottom <= adjustedWindowHeight
      );
    };

    if (!checkVisibility()) {
      requestAnimationFrame(() => {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }, [getElementRef]);

  const setHighlight = useCallback((index: number) => {
    if (index < 0 || index >= audioItems.length) {
      log.error('高亮显示索引无效');
      return;
    }

    if (lastHighlightedRef.current) {
      const lastElem = getElementRef(lastHighlightedRef.current);
      if (lastElem) lastElem.style.color = "#333333";
    }

    const currentItem = audioItems[index];
    const curElement = getElementRef(currentItem.id);
    if (curElement) {
      curElement.style.color = "#32CD32";
      lastHighlightedRef.current = currentItem.id;
      scrollToElement(lastHighlightedRef.current);
    }
  }, [audioItems, getElementRef, scrollToElement]);

  useEffect(() => {
    if (audioItems.length > 0 && currentSentence === 0) {
      setHighlight(currentSentence);
    }
  }, [audioItems, setHighlight]);

  useEffect(() => {
    if (!checkIsLogin(false)) return;
    if (!checkIsMember(false)) return;
    
    if (audioItems.length > 0 && currentSentence < audioItems.length) {
      if (audioItems[currentSentence] && audioItems[currentSentence].audio) {
        initHowl();
      }
    }

    return () => {
      audioManager.stopAndUnloadCurrentAudio();
      audioRef.current = null;
    };
  }, [audioItems, currentSentence, lessonId]);

  const handleChatbotClick = async () => {
    if (!checkIsLogin(true)) return;
    if (!checkIsMember(true)) return;
    
    const canUse = await canUseAIAssistant(memberType);
    if (!canUse) {
      const maxUsage = await getAIAssistantMaxUsage(memberType);
      const currentUsage = await getAIAssistantUsageToday();
      message.warning(`您今日的AI助教使用次数已达上限（${currentUsage}/${maxUsage}次）`);
      return;
    }
    
    setIsChatbotOpen(true);
  };

  const handleChatbotClose = () => {
    setIsChatbotOpen(false);
  };

  return (
    <>
      {isMobile?(
        <FloatButton.Group
          trigger="click"
          type="primary"
          className={styles.playerGroup}
          icon={<ControlOutlined />}
        >
          <div className={styles.speedButton}>
            <Select
              value={playbackSpeed.toString()}
              onChange={changeSpeed}
              options={SPEED_OPTIONS}
              placement="topLeft"
              popupMatchSelectWidth={false}
            />
          </div>
          <div className={styles.controlButtons}>
            <FloatButton
              icon={<BackwardOutlined />}
              onClick={prevSentence}
              tooltip="上一句"
            />
            <FloatButton
              icon={isPlaying ? <PauseCircleTwoTone /> : <PlayCircleTwoTone />}
              onClick={togglePlayPause}
              tooltip={isPlaying ? "暂停" : "播放"}
              className={styles.playButton}
            />
            <FloatButton
              icon={<ForwardOutlined />}
              onClick={nextSentence}
              tooltip="下一句"
            />
          </div>
          <div className={styles.utilityButtons}>
            <FloatButton
              icon={<EditOutlined />}
              onClick={editClick}
              tooltip="编辑笔记"
            />
            <FloatButton
              icon={<BookOutlined />}
              onClick={showNoteDrawer}
              tooltip="查看笔记"
            />
            <FloatButton
              icon={<RobotOutlined />}
              onClick={handleChatbotClick}
              tooltip="AI助教"
            />
          </div>
        </FloatButton.Group>
      ):(
        <Flex justify="center" align="center" className={styles.playerContainer} ref={playerContainerRef} gap={24}>
          <Select
              value={playbackSpeed.toString()}
              onChange={changeSpeed}
              options={SPEED_OPTIONS}
              className={styles.speedSelect}
              placement="topLeft"
              popupMatchSelectWidth={false}
            />
            <BackwardOutlined className={styles.controlIcon} onClick={prevSentence} />
            <div className={styles.playPauseButton} onClick={togglePlayPause}>
              {isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            </div>
            <ForwardOutlined className={styles.controlIcon} onClick={nextSentence} />
            <EditOutlined className={styles.controlIcon} onClick={editClick} />
            <BookOutlined className={styles.controlIcon} onClick={showNoteDrawer} />
            <Button className={styles.speedSelect} onClick={handleChatbotClick}>AI助教</Button>
        </Flex>
      )}
      <Drawer
        title="笔记"
        onClose={onCloseDrawer}
        open={isNoteOpen}
        placement="right"
        width={window.innerWidth < 768 ? '100%' : 400}
        className={styles.drawer}
      >
        <pre className={styles.pre}>{localNotes}</pre>
      </Drawer>

      <Modal
        title="编辑笔记"
        open={isEditNoteOpen}
        okText="保存"
        cancelText="取消"
        onOk={handleOk}
        onCancel={handleCancel}
        width={window.innerWidth < 768 ? '90%' : '50%'}
        destroyOnClose
      >
        <Input.TextArea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          rows={6}
          autoSize={{ minRows: 6, maxRows: 12 }}
          placeholder="在这里记笔记..."
        />
      </Modal>

      <Chatbot open={isChatbotOpen} onClose={handleChatbotClose} />
    </>
  );
};

export default TextPlayer;